import type { IModuleGroupManifest } from '@platform/core/types/modularity/IModuleGroupManifest';
import type { IModuleManifest } from '@platform/core/types/modularity/IModuleManifest';

/** Запасна група адреси власного модуля, категорія якого не має пакета групи (ADR-028): `/custom/<ключ>`. */
export const CUSTOM_GROUP = 'custom';

const byKey = new Map<string, IModuleManifest>();
const keyBySlug = new Map<string, string>();
const groupOfCategory = new Map<string, string>();
const customByWorkspace = new Map<string, ReadonlyMap<string, string>>();

/**
 * Адреси модулів (ADR-028): `/<група>/<модуль>/<сутність>/…` будуються з ключів модуля й типу запису, а маршрут повертає ключі за сегментами.
 * Опубліковані модулі реєструє registerModuleGroups (slug групи, модуля й сутностей — з маніфестів пакетів), власні модулі просторів
 * (ADR-025) — запит каталогу: їхній сегмент — сам ключ (mod_…), група — пакет групи їхньої категорії або `custom`. Файл без залежностей,
 * тож будівники маршрутів (routes) не імпортують пакетів.
 */
export function registerModulePaths(groups: readonly IModuleGroupManifest[]): void {
  byKey.clear();
  keyBySlug.clear();
  groupOfCategory.clear();
  for (const group of groups) {
    groupOfCategory.set(group.category, group.key);
    for (const module of group.modules) {
      byKey.set(module.key, module);
      keyBySlug.set(module.slug, module.key);
    }
  }
}

/** Замінює власні модулі простору (ключ → категорія); зміна категорії змінює лише канонічну групу, ключ модуля лишається сегментом. */
export function registerCustomModulePaths(workspaceId: string, modules: readonly { key: string; category: string }[]): void {
  if (modules.length) customByWorkspace.set(workspaceId, new Map(modules.map((m) => [m.key, m.category])));
  else customByWorkspace.delete(workspaceId);
}

function customCategory(key: string): string | undefined {
  for (const list of customByWorkspace.values()) if (list.has(key)) return list.get(key);
  return undefined;
}

/** Чи модуль відомий (опублікований пакет або зареєстрований власний модуль) — лише тоді канонічна адреса остаточна. */
export const isKnownModule = (key: string): boolean => byKey.has(key) || customCategory(key) !== undefined;

/** Канонічний шлях модуля: `/<slug групи>/<slug модуля>`; власний чи невідомий модуль — `/<група категорії | custom>/<ключ>`. */
export function modulePath(key: string): string {
  const manifest = byKey.get(key);
  if (manifest) return `/${manifest.group}/${manifest.slug}`;
  const category = customCategory(key);
  return `/${(category !== undefined && groupOfCategory.get(category)) || CUSTOM_GROUP}/${key}`;
}

/** Сегмент сутності (типу запису) у шляху модуля; тип поза маніфестом і тип власного модуля — сам ключ. */
export const entitySegment = (moduleKey: string, rtKey: string): string =>
  byKey.get(moduleKey)?.entities.find((e) => e.key === rtKey)?.slug ?? rtKey;

/** Ключ модуля за сегментом адреси: slug опублікованого модуля, інакше сегмент — ключ (власний модуль, ADR-025). */
export const moduleKeyOf = (segment: string): string => keyBySlug.get(segment) ?? segment;

/**
 * Ключ типу запису за сегментом під модулем або undefined, якщо сегмент — сторінка модуля: slug сутності маніфесту (або її ключ);
 * у власного чи ще не відомого модуля єдиний тип має ключ модуля.
 */
export function entityKeyOf(moduleKey: string, segment: string): string | undefined {
  const manifest = byKey.get(moduleKey);
  if (!manifest) return segment === moduleKey ? segment : undefined;
  return manifest.entities.find((e) => e.slug === segment || e.key === segment)?.key;
}
